import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { ArrowRight, Lock, Mail, Radar, ShieldCheck, Zap } from "lucide-react";
import { Brand } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { user, login, loginDraft } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(loginDraft.email);
  const [password, setPassword] = useState(loginDraft.password);
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const loginEmail = String(formData.get("username") || email).trim();
    const loginPassword = String(formData.get("password") || password);
    setErr("");
    setBusy(true);
    try {
      await login(loginEmail, loginPassword, remember);
      nav("/");
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[1.15fr_1fr]">
      <a
        href="https://glazytracker.my.id"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-4 top-4 z-50 inline-flex items-center gap-2 rounded-full border border-yellow-500/50 bg-[#111318]/95 px-4 py-2.5 text-xs font-extrabold tracking-wide text-yellow-400 shadow-lg transition hover:bg-yellow-500 hover:text-black sm:right-6 sm:top-6 sm:px-5 sm:text-sm"
        aria-label="Pasang GPS sekarang, buka website Glazy Tracker"
      >
        <Radar className="h-4 w-4" />
        PASANG GPS SEKARANG
        <ArrowRight className="h-4 w-4" />
      </a>
      <div className="relative hidden overflow-hidden lg:block">
        <img src="/login-hero.jpg" alt="Armada kendaraan di jalan tol" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D12] via-[#0A0D12]/75 to-[#0A0D12]/30" />
        <div className="gt-grid-bg absolute inset-0 opacity-20" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <Brand className="[&_.text-muted-foreground]:text-white/60" />
          <div className="max-w-xl gt-rise">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-yellow-500/40 bg-yellow-500/10 px-3 py-1 text-xs font-semibold text-yellow-400">
              <span className="h-1.5 w-1.5 rounded-full bg-yellow-400 gt-blink" /> Realtime fleet intelligence
            </div>
            <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight xl:text-5xl">Professional Vehicle<br />Tracking Platform.</h1>
            <p className="mt-4 max-w-md text-base text-white/70">Pantau setiap kendaraan secara realtime, putar ulang perjalanan, kelola geofence dan alarm — dalam satu command center yang elegan.</p>
            <div className="mt-8 grid max-w-lg grid-cols-3 gap-3">
              {[[Radar, "Live GPS", "WebSocket"], [ShieldCheck, "RBAC", "Multi-customer"], [Zap, "Alarm", "Instan"]].map(([I, a, b]) => (
                <div key={a} className="rounded-2xl border border-white/10 bg-white/5 p-3 backdrop-blur-md">
                  <I className="h-4 w-4 text-yellow-400" />
                  <div className="mt-2 text-sm font-bold">{a}</div>
                  <div className="text-[11px] text-white/55">{b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} autoComplete="on" className="w-full max-w-sm gt-rise" data-testid="login-form">
          <Brand className="mb-10 lg:hidden" />
          <div className="gt-eyebrow mb-3">Masuk ke akun</div>
          <h2 className="text-3xl font-extrabold tracking-tight">Selamat datang kembali</h2>
          <p className="mt-2 text-sm text-muted-foreground">Gunakan akun yang diberikan oleh administrator GLAZY TRACKER.</p>
          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="gt-eyebrow mb-1.5 block">Email</span>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input type="email" name="username" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="gt-input h-12 pl-10" placeholder="nama@perusahaan.com" data-testid="login-email-input" />
              </div>
            </label>
            <label className="block">
              <span className="mb-1.5 flex items-center justify-between"><span className="gt-eyebrow">Password</span><Link to="/forgot-password" className="text-xs font-semibold gt-brand-text hover:underline" data-testid="login-forgot-link">Lupa password?</Link></span>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input type="password" name="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="gt-input h-12 pl-10" placeholder="••••••••" data-testid="login-password-input" />
              </div>
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="login-remember-checkbox" />
              <span>Ingat Saya (tetap masuk di perangkat ini)</span>
            </label>
            {err && <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-500" data-testid="login-error">{err}</div>}
            <button disabled={busy} className="group flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary font-bold text-primary-foreground transition-[opacity,transform] hover:opacity-95 active:scale-[0.99] disabled:opacity-60" data-testid="login-submit-button">
              {busy ? "Memverifikasi..." : "Masuk"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <Link
              to="/demo"
              className="flex h-12 w-full items-center justify-center rounded-xl border border-primary font-bold text-primary transition-colors hover:bg-primary/10"
              data-testid="login-demo-button"
            >
              Coba Demo Gratis
            </Link>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">Belum punya akun? <Link to="/register" className="font-bold gt-brand-text hover:underline" data-testid="login-register-link">Daftar dengan email</Link></p>
          <p className="mt-4 text-xs text-muted-foreground">Koneksi terenkripsi · Sesi aman berbasis token · Akses berbasis peran</p>
        </form>
      </div>
    </div>
  );
}
